import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, BarChart3, Check, Clock, Filter, Loader2, Lock, Mail, MessageSquare, Pause, Play, Plus, Repeat, Siren, Sparkles, Users, Workflow } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-454" data-mp="e42833e0ol0c">
      <SectionIntro className="mx-auto max-w-3xl text-center" data-id="element-455" data-mp="e3spw1z7ri57">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-456" data-mp="94fswoemouow">
          Workspace
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-457" data-mp="yt3bp2hujpk3">
          One place to manage
          <br data-id="element-458" data-mp="17rfbnv4lrzp7" />
          AI within your company
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-459" data-mp="17u1hnqbui75b">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-460" data-mp="1qhodv50e3rgw">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1.5" data-id="element-461" data-mp="20e9ktvvyzcnd">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-462" data-mp="3pxhv4qkgd72p">
                {/* Selection is carried by an orange rail and a warm fill
                 rather than a floating white card — the card read as a second
                 panel competing with the app frame beside it. */}
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`relative w-full rounded-xl py-4 pl-5 pr-4 text-left transition-[background-color,color] duration-200 ease-out ${on ? 'bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.08)] ring-1 ring-[#EFEAE1]' : 'hover:bg-[#F6F2EA]'}`} data-id="element-463" data-mp="3g03eapumag9v">
                  <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full transition-colors duration-200 ease-out ${on ? 'bg-[#EA580C]' : 'bg-transparent'}`} aria-hidden="true" data-id="element-464" data-mp="7luuo7a2oztu" />
                  <span className="flex items-center gap-2.5" data-id="element-465" data-mp="3tx8sxe8ecngq">
                    <p.icon size={17} strokeWidth={1.9} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-466" data-mp="v9utluhjfwji" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-467" data-mp="qh1pn8tw8s8h">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2.5 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-468" data-mp="fx39nqyfxpgg">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        {/* No app nav on any of the five. The numbered rail to the left is
         already the navigation for this section, so a sidebar inside the frame
         put two columns of it around one screen — and each screen states what
         it is in its own heading anyway. */}
        <AppChrome sidebar={false} data-id="element-469" data-mp="1hh6ywwdnsadb">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className={active === 'chats' ? 'flex h-full flex-col' : 'px-6 py-6 md:px-9 md:py-8'} data-id="element-470" data-mp="1hi9u3gi0kyz1">
            {active === 'chats' && <ChatsScreen data-id="element-471" data-mp="36ylv5qp78iam" />}
            {active === 'shared' && <SharedScreen data-id="element-472" data-mp="2j7yhspwil0go" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-473" data-mp="2j8rjhcgm8zry" />}
            {active === 'pulses' && <PulsesScreen data-id="element-474" data-mp="2e954axh899gi" />}
            {active === 'skills' && <SkillsScreen data-id="element-475" data-mp="2eaga12u8gra9" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-6 border-b border-[#F1ECE3] pb-4" data-id="element-476" data-mp="7ka1re6p08rr">
      <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-477" data-mp="3g29j7110aos">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] tabular-nums text-neutral-500" data-id="element-478" data-mp="2wdom1imi5fe1">
        {meta}
      </p>
    </div>;
}

// The answer, as the thing a revenue lead would actually want on screen: which
// accounts, what they're worth, and the one fact that makes each a risk. Prose
// alone made the reader reassemble a table in their head.
const AT_RISK = [{
  account: 'Northwind Labs',
  value: '$84k',
  closes: '12 Sep',
  reason: 'No exec contact in 41 days · 2 tickets open'
}, {
  account: 'Meridian Health',
  value: '$61k',
  closes: '28 Sep',
  reason: 'Champion left in July · no replacement named'
}, {
  account: 'Fairline Group',
  value: '$37k',
  closes: '30 Sep',
  reason: 'Usage down 38% month over month'
}];
export function ChatsScreen() {
  return <div className="flex h-full min-h-[420px] flex-col" data-id="element-479" data-mp="1utjttac2t90e">
      {/* Thread header — what this chat is and which group owns it. */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#F1ECE3] px-5 py-3.5 md:px-7" data-id="element-480" data-mp="1yttzelvyhxeq">
        <div className="min-w-0" data-id="element-481" data-mp="1mdb25hfplv3y">
          <h3 className="truncate font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-482" data-mp="1medtfqgy1zke">
            Renewal risk in Salesforce
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-483" data-mp="1dcmpor5ys2t6">
            <span className="flex size-4 items-center justify-center rounded bg-[#134E4A] text-[7px] font-bold text-white" data-id="element-484" data-mp="1dez2kpwv7mzd">
              SL
            </span>
            Sales
            <span className="text-[#D8CFC2]" data-id="element-485" data-mp="13li95n0n49w8">·</span>
            Today, 08:42
          </p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-486" data-mp="yo01s129u2qc">
          <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-487" data-mp="plqs1detude0" />
          8 tools connected
        </span>
      </div>

      {/* Transcript */}
      <div className="flex-1 space-y-5 overflow-hidden px-5 py-5 md:px-7" data-id="element-488" data-mp="28kn9spzayl0x">
        <div className="flex justify-end" data-id="element-489" data-mp="28ika1e8cldj0">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-490" data-mp="2dk9n0zf4s19q">
            Which renewals due this quarter look at risk?
          </p>
        </div>

        <div className="flex gap-3" data-id="element-491" data-mp="24i0g0jj2mq13">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#2B211A]" data-id="element-492" data-mp="24l64pxnsfpwa">
            <Sparkles size={13} strokeWidth={2} className="text-white" data-id="element-493" data-mp="1pscat77rr783" />
          </span>
          <div className="min-w-0 flex-1 space-y-3.5" data-id="element-494" data-mp="1umyr9ospjpz1">
            {/* What it read, before what it concluded. */}
            <div className="relative space-y-1.5 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-495" data-mp="1kv2dz6i1vta8">
              {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-496" data-mp="saoxi7wuqt42">
                  <span className="flex size-[18px] shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-497" data-mp="nbm3rjmbo9be">
                    <BrandLogo slug={slug} className="size-[11px]" data-id="element-498" data-mp="x75wjx5larvx" />
                  </span>
                  {label}
                  <Check size={11} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-499" data-mp="1b6ezhv4sbphy" />
                </p>)}
            </div>

            <p className="text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-500" data-mp="1683q1liejh09">
              <strong className="font-semibold text-[#2B211A]" data-id="element-501" data-mp="1kw8aov10m2lo">
                Three of eleven look at risk
              </strong>{' '}
              — $182k of $604k closing this quarter.
            </p>

            <ul className="divide-y divide-[#F1ECE3] overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" data-id="element-502" data-mp="1tyrd5e0w2d7e">
              {AT_RISK.map(r => <li key={r.account} className="flex items-start gap-3 px-3.5 py-2.5" data-id="element-503" data-mp="1pujiz1bnw51s">
                  <span className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-400" data-id="element-504" data-mp="3f9hqmpgk0nou" />
                  <span className="min-w-0 flex-1" data-id="element-505" data-mp="3afdii2vjy9m6">
                    <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-506" data-mp="35h2a7wkwo762">
                      {r.account}
                    </span>
                    <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-507" data-mp="30gfm6drvxbsm">
                      {r.reason}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-508" data-mp="3t8hgg0fsz8ml">
                    <span className="block text-[13px] font-semibold tabular-nums text-[#2B211A]" data-id="element-509" data-mp="3o7teijq1d7dl">
                      {r.value}
                    </span>
                    <span className="block text-[11px] tabular-nums text-neutral-400" data-id="element-510" data-mp="3k685xakjbf97">
                      {r.closes}
                    </span>
                  </span>
                </li>)}
            </ul>

            <p className="text-[13px] text-neutral-500" data-id="element-511" data-mp="28q1g33q453ri">
              I've drafted outreach for each owner — want me to send it?
            </p>
            {/* Offering the next move as buttons is what makes this a working
             surface rather than a transcript of one. */}
            <div className="flex flex-wrap gap-2" data-id="element-512" data-mp="x8fay5u0he2p">
              <span className="rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-semibold text-white" data-id="element-513" data-mp="120f67kz4zisy">
                Send all three
              </span>
              <span className="rounded-lg border border-[#E7E0D4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2B211A]" data-id="element-514" data-mp="16zibn6j13r1g">
                Review drafts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-5 pb-5 md:px-7" data-id="element-515" data-mp="1kzt3ocw2rbo7">
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#E7E0D4] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-516" data-mp="1kx6vqc59uu5g">
          <Plus size={16} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-517" data-mp="3mrz4t8zdkqo5" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-400" data-id="element-518" data-mp="tvxkwhgnkuu4">
            Ask anything across your connected tools
          </span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-519" data-mp="ozfj9qgmdcm0">
            <ArrowUp size={13} strokeWidth={2.6} className="text-white" data-id="element-520" data-mp="12v155ork3ga7" />
          </span>
        </div>
      </div>
    </div>;
}
export function SharedScreen() {
  // Named after the departments any company would recognise. The previous set
  // ("Giga Master", "NoContact Ops") was Giga's own internal workspace, so a
  // visitor had to decode what each one was before the permissions idea landed.
  const groups = [{
    name: 'Leadership',
    color: '#1E3A5F',
    members: 5,
    chats: 96,
    tracks: 14
  }, {
    name: 'Sales',
    color: '#134E4A',
    members: 7,
    chats: 214,
    tracks: 33
  }, {
    name: 'Marketing',
    color: '#7C2D12',
    members: 6,
    chats: 88,
    tracks: 12
  }, {
    name: 'Customer Success',
    color: '#3F3A2E',
    members: 4,
    chats: 131,
    tracks: 21
  }];
  return <div data-id="element-521" data-mp="2f7ky3mcm3k1c">
      <ScreenHead title="Groups" meta="Who can see what — members get every track, chat and connection inside" data-id="element-522" data-mp="2f7bcsc2ii11z" />
      <ul className="space-y-2.5" data-id="element-523" data-mp="20b3qn67jmpk2">
        {groups.map(g => <li key={g.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-524" data-mp="20cet92p95r9l">
            {/* A glyph, not initials. Two letters in a coloured tile read as
             somebody's avatar, and a group is not a person — the people icon
             says "several of us" at a glance. Squared off, where a person's
             avatar is round, so the two never get confused. */}
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
          backgroundColor: g.color
        }} data-id="element-525" data-mp="1hlnn0a0kagzf">
              <Users size={15} strokeWidth={2} className="text-white" data-id="element-526" data-mp="1ckq7y6aum325" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-527" data-mp="1cmkicqohucrz">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-528" data-mp="15ht7qiyhspox">
                {g.name}
              </span>
              {/* Counts, labelled. Three bare numbers in identical chips gave
               no clue what any of them counted. */}
              <span className="mt-0.5 block truncate text-[11.5px] tabular-nums text-neutral-500" data-id="element-529" data-mp="qsnav1n4nbk7">
                {g.members} people
                <span className="mx-1.5 text-[#D8CFC2]" data-id="element-530" data-mp="ms391krlzjrq">·</span>
                {g.tracks} tracks
                <span className="mx-1.5 text-[#D8CFC2]" data-id="element-531" data-mp="hqxf5jm7pqgm">·</span>
                {g.chats} chats
              </span>
            </span>
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-532" data-mp="ct49crotmygv">
              <Lock size={10} className="text-neutral-400" data-id="element-533" data-mp="276dde3yllrrq" />
              Members only
            </span>
          </li>)}
      </ul>
    </div>;
}
const OWNER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/b394cd62-6cbf-40a2-98a1-4af4a23e89dd.jpg';

// Every step names the tools it touches — a step with no marks left a visible
// gap in the column and read as a missing image rather than a deliberate blank.
const STEP_TOOLS: string[][] = [['jira', 'linear'], ['intercom', 'salesforce'], ['notion'], ['slack', 'gmail']];
export function WorkflowsScreen() {
  const steps = ['Pull what shipped, slipped and stalled from Jira and Linear', 'Check every account flagged in support or gone quiet', 'Draft the update into the team space — wins, risks, decisions', 'Post to #team-update and email the leadership list'];
  return <div data-id="element-534" data-mp="20szmbkfy0i64">
      {/* Name, then one line of what it produces, then the health of it as
       tags on the right. You should not have to read the steps to know why
       this workflow exists. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-[#F1ECE3] pb-5" data-id="element-535" data-mp="3c865mu47g0oz">
        <div className="min-w-0" data-id="element-536" data-mp="2rghgyyknhcrz">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-537" data-mp="2rfg3kng5we73">
            Monday morning update
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6B5D52]" data-id="element-538" data-mp="31enfr7b5u2sm">
            Every Monday, 08:00
            <span className="text-[#D8CFC2]" data-id="element-539" data-mp="2wfkn6mcd9p1j">·</span>
            <BrandLogo slug="slack" className="size-3.5" data-id="element-540" data-mp="2wdzrgtebg023" />
            <span className="font-medium text-[#2B211A]" data-id="element-541" data-mp="28r1e44lht88e">#team-update</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-542" data-mp="23shpfwfilicc">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/20 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-700" data-id="element-543" data-mp="2igkzbgwohe6f">
            <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-544" data-mp="1pzs47u7t8hga" />
            Active
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-545" data-mp="1l0p0cd3kgh9w">
            <Repeat size={11} className="text-neutral-400" data-id="element-546" data-mp="gtjwn4dgu6n" />
            26 runs
          </span>
          {/* The bar earns its place: 96% means more with 25 of 26 ticks
           filled beside it than as a number on its own. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-547" data-mp="3roef4xd46izc">
            <span className="flex items-end gap-[2px]" data-id="element-548" data-mp="o87bgqup8lqk">
              {Array.from({
              length: 8
            }, (_, i) => <span key={i} className={`h-2.5 w-[2.5px] rounded-full ${i === 5 ? 'bg-red-400' : 'bg-emerald-500/80'}`} data-id="element-549" data-mp="a8zbvwumjblu" />)}
            </span>
            96%
          </span>
        </div>
      </div>

      {/* Who owns it and who can see it — the questions the steps can't
       answer. The read/write strip that sat here only restated them. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-550" data-mp="2z1lc28c957m6">
        <span className="flex items-center gap-2.5" data-id="element-551" data-mp="3qowetlrebd2u">
          <img src={OWNER_AVATAR} alt="" aria-hidden="true" className="size-7 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(43,33,26,0.08)]" data-id="element-552" data-mp="3loqkfxtqrch2" />
          <span data-id="element-553" data-mp="3hnyb74kvd7ks">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-554" data-mp="3hnf2meh3zvjf">
              Owner
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-555" data-mp="d8ozmfhcs40f">
              Sarah Chen
            </span>
          </span>
        </span>
        {/* "Shared with" read as the audience for the update — but the update
         goes to #team-update, which the subtitle already says. The useful fact
         is permission: who can run and edit this workflow. Same group tile the
         Groups screen uses, so a group reads as one object everywhere. */}
        <span className="flex items-center gap-2.5" data-id="element-556" data-mp="wz0pzwu7d9fq">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-[#1E3A5F] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-557" data-mp="s56bd7tgfkni">
            <Users size={13} strokeWidth={2} className="text-white" data-id="element-558" data-mp="14fxccyi3g7kv" />
          </span>
          <span data-id="element-559" data-mp="19fs2v0xyiv5e">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-560" data-mp="19fa8yayrot0n">
              Can run and edit
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-561" data-mp="1df0vu3l6aozw">
              Leadership · 5 people
            </span>
          </span>
        </span>
      </div>

      <p className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-562" data-mp="1ndqgg44genju">
        Steps
      </p>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2.5 before:absolute before:bottom-7 before:left-[30px] before:top-7 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-563" data-mp="216pvy648khre">
        {steps.map((s, i) => <li key={s} className={`relative flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-564" data-mp="263pwk1ibgsb4" data-mpi={i}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[11px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-565" data-mp="374hlep1z1v26">
              {i + 1}
            </span>
            <span className="flex-1 text-[13px] leading-snug text-[#3D3229]" data-id="element-566" data-mp="3l1xts63tvywk">
              {s}
            </span>
            {/* The tools each step touches, stated once where they're used
             rather than duplicated in a strip above the list. */}
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-567" data-mp="3uyiz6sjrfc0x">
              {(STEP_TOOLS[i] ?? []).map(t => <BrandLogo key={t} slug={t} className="size-[15px] opacity-80" data-id="element-568" data-mp="3mhjnrn0elx0" />)}
            </span>
          </li>)}
      </ol>
      {/* Run now is the screen's primary action and was sized like a tag.
       It now matches the buttons used elsewhere in the product screens. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#EFEAE1] pt-4" data-id="element-569" data-mp="hmihszbw3f8s">
        <span className="inline-flex items-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-570" data-mp="rqyzly8xj6f8">
          <Play size={13} fill="currentColor" data-id="element-571" data-mp="hrq7zc1je3w1" />
          Run now
        </span>
        <span className="text-[12px] tabular-nums text-neutral-500" data-id="element-572" data-mp="dqo6spo27hrf">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
export function PulsesScreen() {
  // A pulse only matters if you know where its output lands, so the
  // destination sits on the row with the mark of the tool it posts to.
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    to: '#revenue',
    logo: 'slack',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    to: 'Leadership list',
    logo: 'gmail',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    to: '#support-escalations',
    logo: 'slack',
    // Running right now. State belongs on the thing it describes, not in a
    // banner underneath that made you match a name back to a row.
    running: 'Step 2 of 4 · reading Intercom',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    to: 'Customer success',
    logo: 'microsoftteams',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  // One starts paused, so the control reads as a real state rather than a
  // decorative button — and the paused row visibly stops reporting.
  const [paused, setPaused] = React.useState<Record<string, boolean>>({
    'Churn signal digest': true
  });
  return <div data-id="element-573" data-mp="t6b61seig8n9">
      {/* The subtitle defines the primitive rather than counting rows: someone
       meeting Pulses for the first time needs to know what one is, and the
       list below already shows how many are running. */}
      <ScreenHead title="Pulses" meta="Work that runs on a schedule and reports into the channel that needs it" data-id="element-574" data-mp="28qag5chjbs7s" />
      <ul className="space-y-2.5" data-id="element-575" data-mp="2nf6tnp2d1xfs">
        {pulses.map(p => {
        const off = !!paused[p.name];
        const running = !off && 'running' in p ? (p as {
          running: string;
        }).running : null;
        return <li key={p.name} className={`flex flex-wrap items-center gap-3.5 px-4 py-3.5 ${ROW} ${running ? 'border-[#EA580C]/25 bg-[#FFF8F3]' : ''}`} data-id="element-576" data-mp="2wda67vvdbjvg">
              <span className="min-w-0 flex-1" data-id="element-577" data-mp="36dstz1q1zkin">
                <span className={`block truncate text-[13.5px] font-semibold ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-578" data-mp="36chql30ises2">
                  {p.name}
                </span>
                {/* While a pulse is mid-run, the row reports the run instead of
                 the schedule — the schedule is not the useful fact right now. */}
                {running ? <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] font-medium text-[#C2410C]" data-id="element-579" data-mp="18hu9sop83bzl">
                    <Loader2 size={11} className="shrink-0 animate-spin" data-id="element-580" data-mp="1dfdwmczzkx35" />
                    <span className="truncate" data-id="element-581" data-mp="1ifrwh7rfp0q3">{running}</span>
                  </span> : <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-582" data-mp="1idnrsnlpnxor">
                    {p.cadence}
                    <span className="text-[#D8CFC2]" data-id="element-583" data-mp="prcrle20nysh">·</span>
                    <BrandLogo slug={p.logo} className={`size-3.5 ${off ? 'opacity-40' : ''}`} data-id="element-584" data-mp="poqc8s8vwezp" />
                    <span className="truncate" data-id="element-585" data-mp="unthacov6l7o">{p.to}</span>
                  </span>}
              </span>
              {/* Twelve ticks say "is this healthy" faster than a percentage. */}
              <span className="flex shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3]" data-id="element-586" data-mp="14h1vhh1hugwg">
                {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${off ? 'bg-[#E2DACE]' : h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-587" data-mp="bz69vnj7tjqx" data-mpi={i} />)}
              </span>
              <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-588" data-mp="2l8dwalo582so">
                {off ? <span className="text-neutral-400" data-id="element-589" data-mp="2g76mkh2fwtjd">Paused</span> : running ? <span className="font-semibold text-[#C2410C]" data-id="element-590" data-mp="2bapwhhbok53n">Running</span> : <>
                    <Clock size={11} className="text-neutral-300" data-id="element-591" data-mp="26bdbz38a7a48" />
                    {p.next}
                  </>}
              </span>
              <button type="button" onClick={() => setPaused(s => ({
            ...s,
            [p.name]: !off
          }))} aria-label={off ? `Resume ${p.name}` : `Pause ${p.name}`} className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-[#EFEAE1] bg-white text-[#6B5D52] transition-colors duration-150 ease-out hover:border-[#D8CFC2] hover:text-[#2B211A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/40" data-id="element-592" data-mp="21jbv14064v7v">
                {off ? <Play size={12} fill="currentColor" data-id="element-593" data-mp="18xdq8xj918ca" /> : <Pause size={12} fill="currentColor" data-id="element-594" data-mp="18wc1jq9b8nc3" />}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}

// One sparkle on every row said "AI feature" four times and nothing about the
// skills themselves. A glyph per subject makes the list scannable by what each
// method is for, and the ink tile matches the group tiles on the Groups screen
// so both screens read as the same application.
export function SkillsScreen() {
  // Named as the piece of knowledge itself ("Inbound lead qualification")
  // rather than as a sentence about it ("How we qualify an inbound lead") —
  // a library of nouns scans; a list of sentences has to be read.
  const skills = [{
    name: 'Inbound lead qualification',
    icon: Filter,
    used: 142,
    group: 'Marketing',
    by: 'Priya Nair',
    initials: 'PN',
    color: '#7C2D12',
    created: 'Jun 4'
  }, {
    name: 'Weekly reporting format',
    icon: BarChart3,
    used: 96,
    group: 'Leadership',
    by: 'Sarah Chen',
    initials: 'SC',
    color: '#1E3A5F',
    created: 'May 12'
  }, {
    name: 'P1 incident escalation',
    icon: Siren,
    used: 38,
    group: 'Customer Success',
    by: 'Tom Alvarez',
    initials: 'TA',
    color: '#3F3A2E',
    created: 'Apr 28'
  }, {
    name: 'Renewal email drafting',
    icon: Mail,
    used: 211,
    group: 'Sales',
    by: 'Marcus Lee',
    initials: 'ML',
    color: '#134E4A',
    created: 'Mar 19'
  }];
  return <div data-id="element-595" data-mp="3c6fdbc5ypwkz">
      <ScreenHead title="Skills" meta="How your company does something, written once and reused everywhere" data-id="element-596" data-mp="3c6p431yu07rq" />
      <ul className="space-y-2.5" data-id="element-597" data-mp="3r3py5c4hwetd">
        {skills.map(s => <li key={s.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-598" data-mp="3r1dacic23a8e">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-599" data-mp="3m320w8pob1qp">
              <s.icon size={15} strokeWidth={1.9} className="text-white" data-id="element-600" data-mp="11dmavfya3h24" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-601" data-mp="2uy61fpjqwkcz">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-602" data-mp="2zvxw2rbrykkv">
                {s.name}
              </span>
              {/* Who wrote the method, and which group it belongs to. A skill
               is somebody's expertise made reusable, so the person is the
               credential — freshness matters far less than authorship here. */}
              <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-603" data-mp="3ekkmpp6uqyre">
                <span className="flex size-[17px] shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-white" style={{
              backgroundColor: s.color
            }} data-id="element-604" data-mp="3ikme95ivibsy">
                  {s.initials}
                </span>
                <span className="truncate text-[#6B5D52]" data-id="element-605" data-mp="1t8136kgvy9ey">{s.by}</span>
                <span className="text-[#D8CFC2]" data-id="element-606" data-mp="1y6knkgftzd57">·</span>
                <span className="truncate" data-id="element-607" data-mp="1y7dov1w0tafy">{s.group}</span>
                <span className="text-[#D8CFC2]" data-id="element-608" data-mp="1qmtbp8u2k3zs">·</span>
                <span className="shrink-0 whitespace-nowrap" data-id="element-609" data-mp="1qoe4aswtpcv0">{s.created}</span>
              </span>
            </span>
            {/* Reuse count as a stat, not a badge: the number is the point, so
             it carries the weight and the unit sits under it in small caps. */}
            <span className="shrink-0 text-right" data-id="element-610" data-mp="1bzhw8vurnzue">
              <span className="block text-[15px] font-semibold tabular-nums leading-none text-[#2B211A]" data-id="element-611" data-mp="29lxisc21vnot">
                {s.used}
              </span>
              <span className="mt-1 block text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-612" data-mp="24ndsjzgfbpqn">
                uses
              </span>
            </span>
          </li>)}
      </ul>
    </div>;
}